// UI-2: Card, StatTile and ProgressBar. design-brief.md "Cards", "Stat tiles" and
// "Progress bars". The category shows in the header bar (a token name), never repeated in
// the body. The lift is for hover devices only (v2.css), and off with reduced motion.
import React from 'react';
import { v } from './tokens';
import { DashboardCard } from './DashboardCard';
import './v2.css';

const cx = (...parts) => parts.filter(Boolean).join(' ');

// color: the category pastel for the header bar (e.g. 'pastel-mint').
// label / meta: the bar's left and right. footer: the action row. lift: hover lift.
// section (decision 89): a page's SECTION card wears the Dashboard's DashboardCard header: the
// coloured strip, the label as its Poppins title at --text-dcard-title (an h2), no emoji. A card that
// does not lift and has a label is a section by default; a lifting card (a list item, whose label is
// a category) is not. Say section outright for a lifting section, or section={false} to opt out.
export function Card({ color, label, meta, title, children, footer, lift = true, inner = false, section, as: Tag = 'article', className, ...rest }) {
  const isSection = section ?? (lift === false && !!label);
  if (isSection && label) {
    return (
      <DashboardCard title={label} tint={color || 'pastel-neutral'} meta={meta || null} actions={footer || null}
        className={cx('ui-card--section', className)} {...rest}>
        {title && <h3 className="ui-card__title">{title}</h3>}
        {children}
      </DashboardCard>
    );
  }
  return (
    <Tag className={cx('ui-card', lift && 'ui-card--lift', inner && 'ui-card--inner', className)} {...rest}>
      {(label || meta) && (
        <div className="ui-card__bar" style={{ '--ui-bg': v(color || 'pastel-neutral') }}>
          {label && <span className="ui-card__label">{label}</span>}
          {meta && <span className="ui-card__meta">{meta}</span>}
        </div>
      )}
      <div className="ui-card__body">
        {title && <h3 className="ui-card__title">{title}</h3>}
        {children}
      </div>
      {footer && <div className="ui-card__footer">{footer}</div>}
    </Tag>
  );
}

// Inside a card (default) or stand-alone with an 8px pastel strip (card).
export function StatTile({ label, value, hint, card = false, color = 'pastel-blue' }) {
  return (
    <div className={cx('ui-stat', card && 'ui-stat--card')} style={card ? { '--ui-strip': v(color) } : undefined}>
      <span className="ui-stat__label">{label}</span>
      <span className="ui-stat__value">{value}</span>
      {hint && <span className="ui-stat__hint">{hint}</span>}
    </div>
  );
}

const pct = (n, of) => (of > 0 ? Math.max(0, Math.min(100, (n / of) * 100)) : 0);

// Single: value of max, ink fill (or a pastel with an ink outline).
// Stacked: segments [{ label, value, color }] of max, with a legend.
export function ProgressBar({ label, value = 0, max = 100, valueText, color, segments }) {
  const stacked = Array.isArray(segments);
  const total = stacked ? segments.reduce((s, x) => s + x.value, 0) : value;
  const text = valueText ?? `${Math.round(pct(total, max))}%`;
  return (
    <div className="ui-progress">
      {(label || text) && (
        <div className="ui-progress__head"><span>{label}</span><span>{text}</span></div>
      )}
      <div className="ui-progress__track" role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={max} aria-valuenow={total} aria-valuetext={text}>
        {stacked
          ? segments.map((s) => (
            <span key={s.label} className="ui-progress__fill ui-progress__fill--pastel" style={{ width: `${pct(s.value, max)}%`, '--ui-bg': v(s.color) }} />
          ))
          : <span className={cx('ui-progress__fill', color && 'ui-progress__fill--pastel')} style={{ width: `${pct(value, max)}%`, ...(color ? { '--ui-bg': v(color) } : {}) }} />}
      </div>
      {stacked && (
        <div className="ui-progress__legend">
          {segments.map((s) => (
            <span key={s.label}><span className="ui-progress__key" style={{ '--ui-bg': v(s.color) }} aria-hidden="true" />{s.label} {s.value}</span>
          ))}
        </div>
      )}
    </div>
  );
}
